import _ from "lodash";
import React, { Fragment } from "react";
import classnames from "classnames";
import { FormattedMessage } from "react-intl";

import { TableLoader } from "./tableLoader/TableLoader";
import { RefreshButton } from "../../refreshButton/RefreshButton";

const isEven = (num) => num % 2 === 0;

export const TBody = ({
  noDataMessageID,
  noFilteredMessageID,
  renderRowSubComponent,
  showPagination,
  tableProps,
  isLoading,
  isLoadFailed,
  onRefreshClick,
}) => {
  const {
    data,
    globalFilteredRows,
    page,
    prepareRow,
    getTableBodyProps,
    rows,
    toggleRowExpanded,
    visibleColumns,
  } = tableProps;
  const tableBodyProps = _.omit(getTableBodyProps(), "role");

  if (isLoading) {
    return (
      <tbody {...tableBodyProps}>
        {<TableLoader visibleColumnsLength={visibleColumns.length} />}
      </tbody>
    );
  }

  if (isLoadFailed) {
    return (
      <tbody {...tableBodyProps}>
        {
          <TableDataFailure
            visibleColumns={visibleColumns}
            onRefreshClick={onRefreshClick}
          />
        }
      </tbody>
    );
  }

  if (_.isEmpty(data) || _.isEmpty(globalFilteredRows)) {
    return (
      <tbody {...tableBodyProps}>
        <NoDataRow
          visibleColumns={visibleColumns}
          noDataMessageID={
            _.isEmpty(data) ? noDataMessageID : noFilteredMessageID
          }
        />
      </tbody>
    );
  }

  const items = showPagination ? page : rows;

  return (
    <tbody {...tableBodyProps}>
      {items.map((row, index) => {
        prepareRow(row);

        const className = isEven(index + 1) ? "--even" : "--odd";

        if (renderRowSubComponent) {
          return (
            <ExpandableRow
              key={row.id}
              row={row}
              className={className}
              renderRowSubComponent={renderRowSubComponent}
              toggleRowExpanded={toggleRowExpanded}
              visibleColumns={visibleColumns}
            />
          );
        }

        return <Row key={row.id} row={row} className={className} />;
      })}
    </tbody>
  );
};

const TableDataFailure = ({ visibleColumns, onRefreshClick }) => {
  return (
    <tr className="no-data-row">
      <td colSpan={visibleColumns.length} className={"table-data-error"}>
        <FormattedMessage id={"loading_data_failure"} />
        <RefreshButton onClick={onRefreshClick} />
      </td>
    </tr>
  );
};

const NoDataRow = ({ visibleColumns, noDataMessageID }) => (
  <tr className="no-data-row">
    <td colSpan={visibleColumns.length}>
      <FormattedMessage id={noDataMessageID} />
    </td>
  </tr>
);

const Row = ({ className, row, onClick }) => (
  <tr
    className={className}
    {..._.omit(row.getRowProps(), "role")}
    onClick={onClick}
    data-testid="table-row"
  >
    {row.cells.map((cell, i) => (
      <Cell key={i} cell={cell} />
    ))}
  </tr>
);

const ExpandableRow = ({
  className,
  renderRowSubComponent,
  row,
  toggleRowExpanded,
  visibleColumns,
}) => (
  <Fragment>
    <Row
      row={row}
      className={classnames(className, "expand-row", {
        "expand-row__expanded": row.isExpanded,
      })}
      onClick={() => toggleRowExpanded(row.id)}
    />
    {row.isExpanded ? (
      <SubRow
        renderRowSubComponent={renderRowSubComponent}
        visibleColumns={visibleColumns}
        row={row}
      />
    ) : null}
  </Fragment>
);

const Cell = ({ cell }) => (
  <td
    className={cell.column.cellClassName}
    {..._.omit(cell.getCellProps(), "role")}
  >
    {cell.render("Cell")}
  </td>
);

const SubRow = ({ renderRowSubComponent, row, visibleColumns }) => (
  <tr className="sub-row">
    <td colSpan={visibleColumns.length}>{renderRowSubComponent({ row })}</td>
  </tr>
);
